import { Validation } from './Validation';
import CategoryTypeJson from './CategoryTypeJson';

export default interface PriceJson {
   ID: number | null;
  
   /**
    * Name des Preises.
    */
   title: string;

   /**
    * Besondere Hinweise, die für diesen Preis oder diese Vergünstigung gelten. Kann ein leerer String sein wenn nichts besonderes gilt.
    */
   remarks: string;

   /**
     * Wenn true, wird der Preis nur für die Verwaltung angezeigt.
     */
   isManagementOnly: boolean;

   /**
     * Ist ein Preis auf Anfrage, ist das auf true gesetzt. Dann soll das Frontend bitte den obigen "price" nicht anzeigen. Er existiert aber trotzdem schon, damit die Verwaltung was vorausgefüllt haben kann.
     */
   isOnRequestOnly: boolean;

   /**
     * Gibt an für welche Typen von Kunden diese Bedingungen gelten. Wenn der type null ist, kann jeder (auch ohne Account) diese Bedingungen buchen
     */
   restrictToTypes: CategoryTypeJson[] | null;

   /**
    * Gibt an, ob die Stornierungsgebühr pro Teilnehmer gilt.
    */
   cancellationFeePerParticipant: boolean,

   /**
    * Stornierungsgebuer in Eurocent.
    */
   cancellationFee: number,

   /**
     * Gibt an, wie welcher Anteil an Mehrwertsteuer in der Stornierungsgebuer enthalten ist.  0-100%
     */
   cancellationFeeIncludedVATRate: number;

   /**
    * Stornierungsbedingungen.
    */
   cancellationRemarks: string,

   /**
    * Preis pro Teilnehmer für diese Veranstaltung in Eurocent der als Anzahlung fällig ist.
    */
   priceDeposit: number,

   /**
    * Betrag zu dem diese Veranstaltung angeboten wird in Eurocent. Gilt für alle weiteren Teilnehmenden.
    */
   priceDepositAdditionalParticipant: number,

   /**
    * Teilnehmerzahl bis zu der priceDeposit gilt. Bei null wird immer der priceDeposit berechnet.
    */
   priceDepositParticipantCount: number | null;

   /**
     * Gibt an, wie welcher Anteil an Mehrwertsteuer in den Anzahlungen enthalten ist. 0-100%
     */
   priceDepositIncludedVATRate: number;

   /**
    * Preis pro Teilnehmer für dieses Programm in Eurocent der nicht als Anzahlung fällig ist. 
    * Der Gesamtpreis des Kurses pro Teilnehmer ist priceDeposit + priceNonDeposit
    */
   priceNonDeposit: number,

   /**
    * Restbetrag zu dem diese Veranstaltung angeboten wird in Eurocent. Gilt für alle weiteren Teilnehmenden.
    */
   priceNonDepositAdditionalParticipant: number,

   /**
    * Teilnehmerzahl bis zu der priceNonDeposit gilt. Bei null wird immer der priceNonDeposit berechnet.
    */
   priceNonDepositParticipantCount: number | null;

   /**
     * Gibt an, wie welcher Anteil an Mehrwertsteuer in den Restbetraegen enthalten ist.  0-100%
     */
   priceNonDepositIncludedVATRate: number;

   /**
    * Gibt für das Kunden-Buchungsformular an, in welche Klasse die Teilnehmenden für den Preis mindestens gehen müssen. Kann null sein, falls keine Einschränkung besteht.
    */
   participantClassMinimum: number | null;

   /**
    * Gibt für das Kunden-Buchungsformular an, in welche Klasse die Teilnehmenden für den Preis maximal gehen dürfen. Kann null sein, falls keine Einschränkung besteht.
    */
   participantClassMaximum: number | null;

   /**
    * Gibt an, dass dieser Tarif nur verfügbar ist, wenn alle Erziehnungsberechtigten berufstätig sind.
    */
   requiresAllGuardiansEmployed: boolean;

   /**
     * Gibt an, dass zusätzliche Angaben zwingend erforderlich sind (realisiert durch zusätzliches Textfeld, welches dann Pflicht ist).
     */
   requiresAdditionalInfo: boolean;
}

export type PriceBookingJson = Pick<PriceJson, "ID" | "title" | "remarks" | "isOnRequestOnly" | "cancellationFeePerParticipant" | "cancellationFee" | "cancellationFeeIncludedVATRate" | "cancellationRemarks" | "priceDeposit" | "priceDepositAdditionalParticipant" | "priceDepositParticipantCount" | "priceDepositIncludedVATRate" | "priceNonDeposit" | "priceNonDepositAdditionalParticipant" | "priceNonDepositParticipantCount" | "priceNonDepositIncludedVATRate">;

export function defaultPriceJson(): PriceJson {
  return {
      ID: null,
      title: "",
      remarks: "",
      isManagementOnly: false,
      isOnRequestOnly: false,
      restrictToTypes: null,
      cancellationFeePerParticipant: false,
      cancellationFee: 0,
      cancellationFeeIncludedVATRate: 0,
      cancellationRemarks: "",
      priceDeposit: 0,
      priceDepositAdditionalParticipant: 0,
      priceDepositParticipantCount: null,
      priceDepositIncludedVATRate: 0,
      priceNonDeposit: 0,
      priceNonDepositAdditionalParticipant: 0,
      priceNonDepositParticipantCount: null,
      priceNonDepositIncludedVATRate: 0,
      participantClassMinimum: null,
      participantClassMaximum: null,
      requiresAllGuardiansEmployed: false,
      requiresAdditionalInfo: false,
  };
}

export function defaultPriceBookingJson(): PriceBookingJson {
  return {
      ID: null,
      title: "",
      remarks: "",
      isOnRequestOnly: false,
      cancellationFeePerParticipant: false,
      cancellationFee: 0,
      cancellationFeeIncludedVATRate: 0,
      cancellationRemarks: "",
      priceDeposit: 0,
      priceDepositAdditionalParticipant: 0,
      priceDepositParticipantCount: null,
      priceDepositIncludedVATRate: 0,
      priceNonDeposit: 0,
      priceNonDepositAdditionalParticipant: 0,
      priceNonDepositParticipantCount: null,
      priceNonDepositIncludedVATRate: 0,
  };
}

export function getPricesFilteredByRestrictToTypes(prices: PriceJson[], categoryTypes: CategoryTypeJson[] | null): PriceJson[] {
  return prices.filter((i: PriceJson) => {
    if (i.restrictToTypes == null) {
      return true;
    } else {
      if (categoryTypes != null) {
        return categoryTypes.length > 0
          ? i.restrictToTypes.some((i) => categoryTypes.map(c => c.ID).includes(i.ID))
          : false;
      } else {
        return false;
      }
    }
  });
}

export function getCheapestPrice(prices: PriceJson[]): PriceJson | undefined {
  let filteredPrices = prices.filter((i: PriceJson) => i.isOnRequestOnly == false);

  if (filteredPrices.length > 0) {
    let cheapest = filteredPrices.reduce(
      (acc: PriceJson, i: PriceJson) => {
        let accTotal = acc.priceDeposit + acc.priceNonDeposit;
        let iTotal = i.priceDeposit + i.priceNonDeposit;
        if(accTotal < iTotal) {
          return acc
        } else {
          return i;
        }
      }
    );

    return cheapest;
  }

  return undefined;
}

export function validateEventPriceJsonAddNew(item: PriceJson) {
  return new Validation(item)
     .validateEquals("ID", null)
     .validateNonEmptyString("title", 100)
     .validateString("remarks", 500)
     .validateInteger("cancellationFee", 0)
     .validateNumber("cancellationFeeIncludedVATRate", 0, 100)
     .validateString("cancellationRemarks", 500)
     .validateInteger("priceDeposit", 0)
     .validateInteger("priceDepositAdditionalParticipant", 0)
     .validateInteger("priceDepositParticipantCount", 0, null, true)
     .validateNumber("priceDepositIncludedVATRate", 0, 100)
     .validateInteger("priceNonDeposit", 0)
     .validateInteger("priceNonDepositAdditionalParticipant", 0)
     .validateInteger("priceNonDepositParticipantCount", 0, null, true)
     .validateNumber("priceNonDepositIncludedVATRate", 0, 100)
     .validateInteger("participantClassMinimum", 0, 120, true)
     .validateInteger("participantClassMaximum", 0, 120, true)
     .finish();
}

export function validateEventPriceJson(item: PriceJson) {
   return new Validation(item)
      .validateInteger("ID", 0, null, true)
      .validateNonEmptyString("title", 100)
      .validateString("remarks", 500)
      .validateInteger("cancellationFee", 0)
      .validateNumber("cancellationFeeIncludedVATRate", 0, 100)
      .validateString("cancellationRemarks", 500)
      .validateInteger("priceDeposit", 0)
      .validateInteger("priceDepositAdditionalParticipant", 0)
      .validateInteger("priceDepositParticipantCount", 0, null, true)
      .validateNumber("priceDepositIncludedVATRate", 0, 100)
      .validateInteger("priceNonDeposit", 0)
      .validateInteger("priceNonDepositAdditionalParticipant", 0)
      .validateInteger("priceNonDepositParticipantCount", 0, null, true)
      .validateNumber("priceNonDepositIncludedVATRate", 0, 100)
      .validateInteger("participantClassMinimum", 0, 120, true)
      .validateInteger("participantClassMaximum", 0, 120, true)
      .finish();
}

export function validateEventPriceBookingJsonAddNew(item: PriceJson) {
  return new Validation(item)
     .validateEquals("ID", null)
     .validateNonEmptyString("title", 100)
     .validateString("remarks", 500)
     .validateInteger("cancellationFee", 0)
     .validateNumber("cancellationFeeIncludedVATRate", 0, 100)
     .validateString("cancellationRemarks", 500)
     .validateInteger("priceDeposit", 0)
     .validateInteger("priceDepositAdditionalParticipant", 0)
     .validateInteger("priceDepositParticipantCount", 0, null, true)
     .validateNumber("priceDepositIncludedVATRate", 0, 100)
     .validateInteger("priceNonDeposit", 0)
     .validateInteger("priceNonDepositAdditionalParticipant", 0)
     .validateInteger("priceNonDepositParticipantCount", 0, null, true)
     .validateNumber("priceNonDepositIncludedVATRate", 0, 100)
     .finish();
}

export function validateEventPriceBookingJson(item: PriceJson) {
  return new Validation(item)
     .validateInteger("ID", 0)
     .validateNonEmptyString("title", 100)
     .validateString("remarks", 500)
     .validateInteger("cancellationFee", 0)
     .validateNumber("cancellationFeeIncludedVATRate", 0, 100)
     .validateString("cancellationRemarks", 500)
     .validateInteger("priceDeposit", 0)
     .validateInteger("priceDepositAdditionalParticipant", 0)
     .validateInteger("priceDepositParticipantCount", 0, null, true)
     .validateNumber("priceDepositIncludedVATRate", 0, 100)
     .validateInteger("priceNonDeposit", 0)
     .validateInteger("priceNonDepositAdditionalParticipant", 0)
     .validateInteger("priceNonDepositParticipantCount", 0, null, true)
     .validateNumber("priceNonDepositIncludedVATRate", 0, 100)
     .finish();
}